import { DiscountDisplay } from '@/components/promotional-offers/DiscountDisplay';
import { RedemptionTypeDisplay } from '@/components/promotional-offers/RedemptionTypeDisplay';
import { BusinessHoursDisplay } from '@/components/shared/BusinessHoursDisplay';
import { usePromotionalOfferContext } from '@/pages/promotional-offers/usePromotionalOfferContext';
import { EmptyTableState, RichTextContent } from '@startupquickstart/frontend';
import { FileText } from 'lucide-react';
import { formatDate, formatDateTime } from '@startupquickstart/frontend';

export function PromotionalOfferOverviewTab() {
  const { offer } = usePromotionalOfferContext();

  if (!offer) return null;

  const hasHours = offer.hours && Object.keys(offer.hours).length > 0;
  const hasTerms = !!offer.termsAndConditions;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Left column */}
      <div className="lg:col-span-3 space-y-6">
        {/* About card */}
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium tracking-tight text-zinc-900">About</h2>
            <p className="text-sm text-muted-foreground">Offer description and details</p>
          </div>
          <div className="p-6">
            {offer.description ? (
              <RichTextContent content={offer.description} />
            ) : (
              <EmptyTableState
                icon={<FileText className="h-6 w-6" />}
                title="No description"
                description="Edit to add a description"
              />
            )}
          </div>
          <div className="border-t px-6 py-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
            <span>Created: {formatDate(offer.createdAt)}</span>
            <span>Updated: {formatDate(offer.updatedAt)}</span>
          </div>
        </div>

        {/* Discount Information card */}
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium tracking-tight text-zinc-900">
              Discount Information
            </h2>
            <p className="text-sm text-muted-foreground">How the discount is applied</p>
          </div>
          <div className="p-6">
            <DiscountDisplay
              discountType={offer.discountType as 'percentage' | 'fixed_amount' | undefined}
              discountPercentage={offer.discountPercentage}
              discountValue={offer.discountValue}
              maxDiscountAmount={offer.maxDiscountAmount}
            />
          </div>
        </div>

        {/* Validity & Hours card */}
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium tracking-tight text-zinc-900">
              Validity &amp; Hours
            </h2>
            <p className="text-sm text-muted-foreground">When this offer can be redeemed</p>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                  Valid From
                </div>
                <div className="text-sm">
                  {formatDateTime(offer.validFrom) || (
                    <span className="text-muted-foreground">No start date</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                  Valid Until
                </div>
                <div className="text-sm">
                  {formatDateTime(offer.validUntil) || (
                    <span className="text-muted-foreground">No end date</span>
                  )}
                </div>
              </div>
            </div>
            {hasHours && (
              <div className="border-t pt-6">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
                  Valid Hours
                </div>
                <BusinessHoursDisplay
                  value={offer.hours}
                  label24_7="All Day"
                  labelClosed="Not Valid"
                />
              </div>
            )}
          </div>
        </div>

        {/* Terms and Conditions card */}
        {hasTerms && (
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                Terms and Conditions
              </h2>
              <p className="text-sm text-muted-foreground">Legal terms for this offer</p>
            </div>
            <div className="p-6">
              <RichTextContent content={offer.termsAndConditions!} />
            </div>
          </div>
        )}
      </div>

      {/* Right column */}
      <div className="lg:col-span-2 space-y-6">
        {/* Details card */}
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium tracking-tight text-zinc-900">Details</h2>
            <p className="text-sm text-muted-foreground">Configuration and settings</p>
          </div>
          <div className="p-6">
            <div className="divide-y">
              {offer.promotionalCode && (
                <div className="py-3 first:pt-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                    Promotional Code
                  </div>
                  <div className="text-sm font-mono bg-muted/50 inline-block px-2 py-0.5 rounded">
                    {offer.promotionalCode}
                  </div>
                </div>
              )}
              <div className="py-3 first:pt-0">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                  Redemption Type
                </div>
                <div className="mt-1">
                  <RedemptionTypeDisplay value={offer.redemptionType} />
                </div>
              </div>
              {(offer.minUsers != null || offer.maxUsers != null) && (
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                    Group Size
                  </div>
                  <div className="text-sm">
                    {offer.minUsers != null && offer.maxUsers != null
                      ? `${offer.minUsers} – ${offer.maxUsers} people`
                      : offer.minUsers != null
                        ? `Min ${offer.minUsers} people`
                        : `Max ${offer.maxUsers} people`}
                  </div>
                </div>
              )}
              {offer.maxUserRedemptions != null && (
                <div className="py-3">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                    Max Redemptions Per User
                  </div>
                  <div className="text-sm">{offer.maxUserRedemptions}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Usage Statistics card */}
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-medium tracking-tight text-zinc-900">
              Usage Statistics
            </h2>
            <p className="text-sm text-muted-foreground">Redemption and usage data</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-muted/30 p-4">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                  Redemptions
                </div>
                <div className="text-2xl font-semibold">
                  {offer.currentRedemptions ?? 0}
                </div>
                {offer.maxRedemptions != null && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    of {offer.maxRedemptions} max
                  </div>
                )}
              </div>
              <div className="rounded-xl bg-muted/30 p-4">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                  Usage Count
                </div>
                <div className="text-2xl font-semibold">
                  {offer.usageCount ?? 0}
                </div>
                {offer.usageLimit != null && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    of {offer.usageLimit} limit
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
